import { FC, useRef } from 'react';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { MdCheck, MdDarkMode, MdDevices } from 'react-icons/md';
import ThemeSwitch, { type ThemeSwitchHandle } from '../ThemeSwitch';
import OnboardingHint from './OnboardingHint';
import { UserMenuCaption } from './UserMenuParts';
import './AppearanceSection.scss';

export interface IAppearanceSectionProps {
    /** Показать онбординг-подсказку «Настройте тему» у переключателя */
    hintOpen?: boolean;
    /** Подсказку закрыли или тему переключили */
    onDismissHint?: () => void;
}

/**
 * Блок «Оформление» меню профиля: текущая тема, анимированный ThemeSwitch и возврат к теме устройства. Схема —
 * Mantine (у ControlPanel — через её менеджер схемы), выбор общий у приложений на одном origin.
 */
const AppearanceSection: FC<IAppearanceSectionProps> = ({ hintOpen = false, onDismissHint }) => {
    const { colorScheme, setColorScheme } = useMantineColorScheme();
    const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect: false });
    const themeSwitchRef = useRef<ThemeSwitchHandle>(null);
    const followsSystemTheme = colorScheme === 'auto';
    const themeTitle = followsSystemTheme
        ? `Как в системе · сейчас ${computedColorScheme === 'dark' ? 'тёмная' : 'светлая'}`
        : computedColorScheme === 'dark'
          ? 'Тёмная тема'
          : 'Светлая тема';
    const followSystemTheme = () => {
        onDismissHint?.();

        if (themeSwitchRef.current) {
            void themeSwitchRef.current.followSystemTheme();
            return;
        }

        setColorScheme('auto');
    };

    return (
        <div className="rf-user-menu-dialog__appearance">
            <UserMenuCaption>Оформление</UserMenuCaption>
            <div className="rf-user-menu-dialog__theme-card">
                <div className="rf-user-menu-dialog__theme-copy">
                    <span className="rf-user-menu-dialog__theme-title" data-testid="user-settings-theme-title">
                        {themeTitle}
                    </span>
                    <span className="rf-user-menu-dialog__theme-description">
                        Нажмите на солнце или луну, чтобы переключить тему
                    </span>
                </div>
                <OnboardingHint
                    opened={hintOpen}
                    icon={<MdDarkMode size={17} />}
                    title="Настройте тему"
                    text="Нажмите на солнце или луну, чтобы переключить оформление"
                    onClose={onDismissHint}
                >
                    <span className="rf-user-menu-dialog__theme-switch-anchor">
                        <ThemeSwitch ref={themeSwitchRef} onToggle={onDismissHint} />
                    </span>
                </OnboardingHint>
            </div>
            <button
                type="button"
                className="rf-user-menu-dialog__system-theme"
                data-testid="user-settings-system-theme"
                aria-pressed={followsSystemTheme}
                onClick={followSystemTheme}
            >
                <MdDevices size="18" aria-hidden="true" />
                <span className="rf-user-menu-dialog__system-theme-label">Следовать теме устройства</span>
                {followsSystemTheme ? (
                    <MdCheck size="17" aria-hidden="true" />
                ) : (
                    <span className="rf-user-menu-dialog__system-theme-state">Включить</span>
                )}
            </button>
        </div>
    );
};

export default AppearanceSection;
